import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

// wantedAdjustmentData 타입 정의
export interface WantedAdjustmentEntry {
  id: number;
  group_id: string;
  year: number;
  month: number;
  nurse_id: string;
  shift_date: string;
  shift_id: string;
  is_applied: boolean;
  source_type: string;
  original_shift_id: string | null;
  reason: string | null;
  head_nurse_memo: string | null;
  created_by: string | null;
  shifts_table_id: number; // shift id임
}

export interface WantedAdjustmentNurse {
  nurse_id: string;
  name: string;
  entries: WantedAdjustmentEntry[];
  monthly_summary: Record<string, number>;
}

export interface WantedAdjustmentAssignment {
  nurse_id?: string;
  startDate?: string;
  endDate?: string | null;
  start_date?: string;
  end_date?: string | null;
  reason: string;
  source_group_id?: string;
  target_group_id?: string;
  target_group_name?: string;
  targetGroupId?: string;
  targetGroupName?: string;
}

export interface WantedAdjustmentData {
  nurses: WantedAdjustmentNurse[];
  assignments?: WantedAdjustmentAssignment[];
  has_fixed_wanted: boolean;
}

function isValidYearMonth(
  year: number | null | undefined,
  month: number | null | undefined
): year is number {
  return year !== undefined && month !== undefined;
}

/**
 * 원티드 반영 설정 데이터 조회 훅
 * @param year 연도
 * @param month 월
 * @param enabled 쿼리 실행 여부 (기본값: true)
 * @returns 원티드 반영 설정 데이터 및 로딩 상태
 */
export function useWantedAdjustmentQuery(
  year: number | null | undefined,
  month: number | null | undefined,
  enabled: boolean = true
) {
  // 타입 가드
  const isValid = isValidYearMonth(year, month);

  return useQuery<WantedAdjustmentData>({
    queryKey: QUERY_KEYS.wanted.adjustment(year as number, month as number),
    queryFn: async () => {
      if (!isValid) {
        throw new Error("필수 항목이 누락 되었습니다.");
      }

      if (month === undefined) {
        throw new Error("필수 항목이 누락 되었습니다.");
      }

      return await httpClient.get<WantedAdjustmentData>(
        `/wanted/adjustment/${year}/${month}`
      );
    },
    enabled: isValid && enabled, // year/month가 유효하고 enabled가 true일 때만 실행
    staleTime: 30 * 1000,
  });
}
